import React, { useState, useMemo, useEffect } from 'react'
import Toast from './Toast'
import GamesTab from './GamesTab'
import PulseTab from './PulseTab'
import GameDetailDrawer from './GameDetailDrawer'
import { useLiveGames, useGameStats, useOdds, usePulse, usePrefetch } from '../hooks/useGames'

// Note: PulseTab is still a destination, accessed via the Pulse indicator in header

const FALLBACK_LEAGUE = 'NBA'

function extractTeamInfo(teamLike, fallbackName, fallbackId) {
  if (!teamLike) {
    return {
      id: fallbackId,
      name: fallbackName,
      score: null,
      quarters: [],
      players: [],
      raw: null
    }
  }

  const quarters = teamLike.quarters || teamLike.linescore || teamLike.periods || []
  const rawPlayers = teamLike.players || teamLike.statistics || []
  
  // Normalize players - handle balldontlie nested structure (NBA + NFL)
  const players = Array.isArray(rawPlayers) ? rawPlayers.map(p => {
    const playerInfo = p.player || p
    // Parse minutes - balldontlie returns "MM:SS" format
    let min = p.min ?? p.minutes ?? 0
    if (typeof min === 'string' && min.includes(':')) {
      min = parseInt(min.split(':')[0]) || 0
    }
    return {
      ...p,
      name: p.name || playerInfo.name || `${playerInfo.first_name || ''} ${playerInfo.last_name || ''}`.trim() || 'Unknown',
      pos: p.pos || p.position || playerInfo.position || null,
      min,
      // NBA stats
      pts: p.pts ?? p.points ?? 0,
      reb: p.reb ?? p.rebounds ?? (p.oreb ?? 0) + (p.dreb ?? 0),
      ast: p.ast ?? p.assists ?? 0,
      stl: p.stl ?? p.steals ?? 0,
      blk: p.blk ?? p.blocks ?? 0,
      to: p.to ?? p.turnovers ?? 0,
      fgm: p.fgm ?? p.field_goals_made ?? 0,
      fga: p.fga ?? p.field_goals_attempted ?? 0,
      fg3m: p.fg3m ?? p.three_pointers_made ?? p.tpm ?? 0,
      fg3a: p.fg3a ?? p.three_pointers_attempted ?? p.tpa ?? 0,
      plusMinus: p.plusMinus ?? p.plus_minus ?? p.pls_min ?? 0,
      three: p.fg3m ?? p.three_pointers_made ?? p.tpm ?? 0,
      // NFL stats - pass through as-is from API
      passYds: p.passYds ?? 0,
      passTd: p.passTd ?? 0,
      int: p.int ?? 0,
      cmp: p.cmp ?? 0,
      att: p.att ?? 0,
      rushYds: p.rushYds ?? 0,
      rushTd: p.rushTd ?? 0,
      rushAtt: p.rushAtt ?? 0,
      rec: p.rec ?? 0,
      recYds: p.recYds ?? 0,
      recTd: p.recTd ?? 0,
      targets: p.targets ?? 0,
    }
  }) : []

  // Extract record if available
  const record = teamLike.record || 
    (teamLike.wins !== undefined && teamLike.losses !== undefined 
      ? `${teamLike.wins}-${teamLike.losses}` 
      : null)

  return {
    id: teamLike.id ?? teamLike.team_id ?? teamLike.teamId ?? teamLike.code ?? fallbackId,
    name:
      teamLike.name ||
      teamLike.full_name ||
      teamLike.nickname ||
      teamLike.team ||
      teamLike.abbreviation ||
      fallbackName,
    code: teamLike.code || teamLike.abbreviation || teamLike.short_name || null,
    score:
      teamLike.score ??
      teamLike.points ??
      teamLike.total ??
      teamLike.runs ??
      teamLike.goals ??
      null,
    record,
    quarters: Array.isArray(quarters) ? quarters : [],
    players,
    raw: teamLike
  }
}

function normalizeGame(game) {
  if (!game) return null

  // Determine league - balldontlie NBA API doesn't include league field
  const league = game.league || game.sport || game.category || 
    (game.home_team?.conference ? 'NBA' : FALLBACK_LEAGUE)
  
  if (league === 'NFL' && game.week) {
    console.log('[normalizeGame] NFL game with week:', { id: game.id, week: game.week, status: game.status })
  }
  
  // Handle teams array format (mock data) or object format (API)
  let homeSource, awaySource
  if (Array.isArray(game.teams)) {
    // Mock data format: teams is an array [home, away]
    homeSource = game.teams[0] || null
    awaySource = game.teams[1] || null
  } else {
    // API format: teams is an object with home/away keys
    // balldontlie uses visitor_team, not away_team
    // Canonical cache uses homeTeam/awayTeam (camelCase)
    homeSource = game.home_team || game.homeTeam || game.home || game.teams?.home || game.teams?.Home || null
    awaySource = game.visitor_team || game.away_team || game.awayTeam || game.away || game.teams?.away || game.teams?.Away || null
  }

  // Check for players in game.players format (from yesterday stats)
  const homePlayers = game.players?.home || homeSource?.players || []
  const awayPlayers = game.players?.away || awaySource?.players || []
  
  const home = extractTeamInfo(
    { ...homeSource, players: homePlayers },
    homeSource?.full_name || homeSource?.name || homeSource?.nickname || 'Home',
    homeSource?.id || homeSource?.team_id || 'home'
  )
  const away = extractTeamInfo(
    { ...awaySource, players: awayPlayers },
    awaySource?.full_name || awaySource?.name || awaySource?.nickname || 'Away',
    awaySource?.id || awaySource?.team_id || 'away'
  )
  
  // Handle scores from different formats
  // NFL canonical format: game.score = { home: X, away: Y }
  if (game.score?.home !== undefined) {
    home.score = game.score.home
  }
  if (game.score?.away !== undefined) {
    away.score = game.score.away
  }
  // Balldontlie format: game.home_team_score, game.visitor_team_score
  if (game.home_team_score !== undefined) {
    home.score = game.home_team_score
  }
  if (game.visitor_team_score !== undefined) {
    away.score = game.visitor_team_score
  }
  
  // Extract quarter scores from balldontlie format (including OT periods)
  // NBA uses: home_q1, home_ot1, etc.
  // NFL uses: home_team_q1, home_team_ot, etc.
  const homeQ1 = game.home_q1 ?? game.home_team_q1
  const visitorQ1 = game.visitor_q1 ?? game.visitor_team_q1
  
  if (homeQ1 !== undefined || visitorQ1 !== undefined) {
    // NBA format
    if (game.home_q1 !== undefined) {
      home.quarters = [game.home_q1, game.home_q2, game.home_q3, game.home_q4].filter(q => q != null)
      away.quarters = [game.visitor_q1, game.visitor_q2, game.visitor_q3, game.visitor_q4].filter(q => q != null)
      // Add NBA OT periods (home_ot1, home_ot2, etc.)
      for (let i = 1; i <= 5; i++) {
        const homeOT = game[`home_ot${i}`]
        const awayOT = game[`visitor_ot${i}`]
        if (homeOT != null || awayOT != null) {
          home.quarters.push(homeOT ?? 0)
          away.quarters.push(awayOT ?? 0)
        }
      }
    } else {
      // NFL format - use 0 for null values to preserve array structure
      home.quarters = [game.home_team_q1 ?? 0, game.home_team_q2 ?? 0, game.home_team_q3 ?? 0, game.home_team_q4 ?? 0]
      away.quarters = [game.visitor_team_q1 ?? 0, game.visitor_team_q2 ?? 0, game.visitor_team_q3 ?? 0, game.visitor_team_q4 ?? 0]
      // Add NFL OT (single OT field)
      if (game.home_team_ot != null || game.visitor_team_ot != null) {
        home.quarters.push(game.home_team_ot ?? 0)
        away.quarters.push(game.visitor_team_ot ?? 0)
      }
    }
  }

  // Normalize status for balldontlie format
  let status = String(game.status || game.phase || game.stage || 'UPCOMING')
  let time = game.time || game.clock || game.status_detail || ''
  
  // balldontlie returns ISO date string for scheduled games
  if (status.includes('T') && status.includes('Z')) {
    const gameTime = new Date(status)
    const now = new Date()
    if (gameTime > now) {
      status = 'SCHEDULED'
      time = gameTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else {
      // Game time has passed but no score - still treat as scheduled or check if live
      const homeScore = game.home_team_score || 0
      const awayScore = game.visitor_team_score || 0
      if (homeScore === 0 && awayScore === 0) {
        status = 'SCHEDULED' // Game hasn't started yet
        time = gameTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      }
    }
  } else if (status === 'Final') {
    status = 'FINAL'
  } else if (status.includes('Qtr') || status.includes('Half') || status.toLowerCase().includes('halftime')) {
    // Live game - "1st Qtr", "2nd Qtr", "3rd Qtr", "4th Qtr", "Halftime"
    const originalStatus = status // Save before overwriting
    status = 'LIVE'
    // Halftime: API returns status='2nd Qtr' but time='Half'
    if (originalStatus.toLowerCase().includes('half') || (game.time && game.time.toLowerCase().includes('half'))) {
      time = 'Halftime'
    } else {
      // Use game.time if it has minutes (e.g., "Q3 5:08"), otherwise use original status
      time = game.time && game.time.includes(':') ? game.time : originalStatus
    }
  } else if (game.period && game.period > 0 && status !== 'FINAL' && !time.toLowerCase().includes('final')) {
    // Game is live (has period but not final)
    status = 'LIVE'
    const periodNum = Number(game.period)
    const isOvertime = Number.isFinite(periodNum) && periodNum >= 5
    const otLabel = isOvertime ? (periodNum === 5 ? 'OT' : `${periodNum - 4}OT`) : `Q${game.period}`
    if (game.time && game.time.includes(':')) {
      // Normalize "Q5 3:12" → "OT 3:12"
      time = isOvertime
        ? String(game.time).replace(/^Q\s*5\b/i, 'OT').replace(/^Q\s*6\b/i, '2OT').replace(/^Q\s*7\b/i, '3OT')
        : game.time
    } else {
      time = otLabel
    }
  } else if (time.toLowerCase().includes('final')) {
    // Game is final - check if it went to OT (period >= 5)
    status = 'FINAL'
    if (game.period >= 5) {
      const otCount = game.period - 4
      time = otCount === 1 ? 'OT' : `${otCount}OT`
    }
  } else if (status.match(/^\d{1,2}\/\d{1,2}/)) {
    // NFL scheduled game format: "12/11 - 8:15 PM EST"
    status = 'SCHEDULED'
    time = game.status?.split(' - ')[1] || game.status || ''
  }

  return {
    id: game.id ?? `${home.name}-${away.name}`,
    league,
    status,
    time,
    period: game.period || null,
    // Preserve NFL OT fields for FINAL/OT detection
    home_team_ot: game.home_team_ot,
    visitor_team_ot: game.visitor_team_ot,
    startsAt: game.datetime || game.start_time || game.scheduled || null,
    venue: game.venue || game.location || null,
    teams: [home, away],
    // Preserve live NFL situation data (down, distance, possession)
    situation: game.situation || null,
    // Preserve odds from API response
    odds: game.odds || null,
    oddsStatus: game.oddsStatus || null,
    // Preserve week field for NFL filtering
    week: game.week || null,
    raw: game
  }
}

function normalizePlayer(player) {
  if (!player) return null

  // Handle balldontlie nested player structure: player.player.first_name
  const playerInfo = player.player || player
  
  const name =
    player.name ||
    playerInfo.name ||
    player.player_name ||
    `${playerInfo.first_name || player.first_name || ''} ${playerInfo.last_name || player.last_name || ''}`.trim()

  const fgMade = player.fgm ?? player.field_goals_made
  const fgAtt = player.fga ?? player.field_goals_attempted
  const threeMade = player.tpm ?? player.three_points_made
  const threeAtt = player.tpa ?? player.three_points_attempted
  const ftMade = player.ftm ?? player.free_throws_made
  const ftAtt = player.fta ?? player.free_throws_attempted

  return {
    ...player,
    name: name || 'Unknown Player',
    pos: player.pos || player.position || playerInfo.position || player.role || null,
    pts: player.points ?? player.pts ?? null,
    reb: player.rebounds ?? player.reb ?? null,
    ast: player.assists ?? player.ast ?? null,
    stl: player.steals ?? player.stl ?? null,
    blk: player.blocks ?? player.blk ?? null,
    fg:
      player.fg ||
      (fgMade != null && fgAtt != null ? `${fgMade}/${fgAtt}` : undefined),
    three:
      player.three ||
      (threeMade != null && threeAtt != null ? `${threeMade}/${threeAtt}` : undefined),
    ft:
      player.ft ||
      (ftMade != null && ftAtt != null ? `${ftMade}/${ftAtt}` : undefined),
    passYds: player.passingYards ?? player.pass_yards ?? player.passing_yards ?? player.passYds ?? null,
    passTd: player.passingTDs ?? player.pass_td ?? player.passing_tds ?? player.passTd ?? null,
    int: player.interceptions ?? player.int ?? player.interceptions_thrown ?? null,
    cmp: player.passingCompletions ?? player.passing_completions ?? player.completions ?? player.cmp ?? null,
    att: player.passingAttempts ?? player.passing_attempts ?? player.attempts ?? player.att ?? null,
    rushAtt: player.rushingAttempts ?? player.rushing_attempts ?? player.rush_attempts ?? player.carries ?? player.rush ?? null,
    rushYds: player.rushingYards ?? player.rush_yards ?? player.rushYds ?? player.rushing_yards ?? null,
    rushTd: player.rushingTDs ?? player.rush_td ?? player.rushing_tds ?? player.rushTd ?? null,
    rec: player.receptions ?? player.catches ?? player.rec ?? null,
    recYds: player.receivingYards ?? player.receiving_yards ?? player.recYds ?? player.yards_receiving ?? null,
    recTd: player.receivingTDs ?? player.receiving_tds ?? player.rec_td ?? player.recTd ?? null,
    targets: player.targets ?? player.receiving_targets ?? null,
    td: player.touchdowns ?? player.td ?? player.total_tds ?? null,
    goals: player.goals ?? player.goal ?? null,
    goalMinutes:
      player.goal_minutes ||
      player.goalMinutes ||
      player.scoring_minutes ||
      player.minute_list ||
      [],
    assists: player.assists ?? player.ast ?? null,
    shots: player.shots ?? player.total_shots ?? null,
    shotsOnTarget: player.shots_on_target ?? player.shotsOnTarget ?? null,
    passes: player.passes ?? player.total_passes ?? null,
    passAccuracy: player.pass_accuracy ?? player.passAccuracy ?? null,
    yellowCards: player.yellow_cards ?? player.yellowCards ?? null,
    redCards: player.red_cards ?? player.redCards ?? null
  }
}

function enrichGameWithStats(game, details) {
  if (!game || !details) return game

  const enriched = {
    ...game,
    // KEEP the normalized status from game, don't overwrite with raw API status
    status: game.status,
    time: game.time
  }

  // Don't use details.home/away if they are arrays (boxscores player data)
  const homeSource =
    details.game?.home_team ||
    details.game?.home ||
    details.home_team ||
    (!Array.isArray(details.home) ? details.home : null) ||
    details.teams?.home ||
    details.teams?.Home
  const awaySource =
    details.game?.away_team ||
    details.game?.away ||
    details.away_team ||
    (!Array.isArray(details.away) ? details.away : null) ||
    details.teams?.away ||
    details.teams?.Away

  if (homeSource || awaySource) {
    const currentHome = extractTeamInfo(
      homeSource,
      game.teams?.[0]?.name || 'Home',
      game.teams?.[0]?.id || 'home'
    )
    const currentAway = extractTeamInfo(
      awaySource,
      game.teams?.[1]?.name || 'Away',
      game.teams?.[1]?.id || 'away'
    )
    // Always preserve quarters from original game (includes OT scores)
    // Original game has the correct quarters from normalizeGame which includes OT
    if (game.teams?.[0]?.quarters?.length > 0) {
      currentHome.quarters = game.teams[0].quarters
    }
    if (game.teams?.[1]?.quarters?.length > 0) {
      currentAway.quarters = game.teams[1].quarters
    }
    enriched.teams = [currentHome, currentAway]
  }

  if (!Array.isArray(enriched.teams)) {
    enriched.teams = game.teams || []
  }

  if (details.playerProps) {
    enriched.playerProps = details.playerProps
  }

  // Handle boxscores format: { home: [], away: [] }
  // IMPORTANT: Only add players, preserve existing team data (scores, name, etc)
  if (Array.isArray(details.home) && Array.isArray(details.away)) {
    const homePlayers = details.home.map(normalizePlayer)
    const awayPlayers = details.away.map(normalizePlayer)
    // Debug: log first player with stats
    const firstWithStats = [...homePlayers, ...awayPlayers].find(p => p?.passYds > 0 || p?.rushYds > 0)
    if (firstWithStats) {
      console.log('[enrichGameWithStats] First player with stats:', firstWithStats.name, 'passYds:', firstWithStats.passYds, 'rushYds:', firstWithStats.rushYds)
    }
    
    if (enriched.teams?.[0]) {
      // Preserve all existing team data, just add players
      enriched.teams[0] = { ...enriched.teams[0], players: homePlayers }
    }
    if (enriched.teams?.[1]) {
      // Preserve all existing team data, just add players
      enriched.teams[1] = { ...enriched.teams[1], players: awayPlayers }
    }
    return enriched
  }

  if (Array.isArray(details.player_stats)) {
    const grouped = [[], []]

    details.player_stats.forEach((player) => {
      const normalisedPlayer = normalizePlayer(player)
      const teamId =
        player.team_id ??
        player.teamId ??
        player.team?.id ??
        player.team?.team_id ??
        player.team
      const teamName =
        (player.team?.name || player.team_name || player.team || '').toString().toLowerCase()

      const [homeTeam, awayTeam] = enriched.teams
      const matchesHome = [
        homeTeam?.id,
        homeTeam?.code,
        homeTeam?.raw?.id,
        homeTeam?.raw?.team_id,
        homeTeam?.raw?.teamId
      ]
        .filter(Boolean)
        .map((value) => value.toString().toLowerCase())
        .includes(teamId?.toString().toLowerCase())

      const matchesHomeByName =
        homeTeam?.name && teamName && homeTeam.name.toLowerCase() === teamName

      const matchesAway = [
        awayTeam?.id,
        awayTeam?.code,
        awayTeam?.raw?.id,
        awayTeam?.raw?.team_id,
        awayTeam?.raw?.teamId
      ]
        .filter(Boolean)
        .map((value) => value.toString().toLowerCase())
        .includes(teamId?.toString().toLowerCase())

      const matchesAwayByName =
        awayTeam?.name && teamName && awayTeam.name.toLowerCase() === teamName

      if (matchesHome || matchesHomeByName) {
        grouped[0].push(normalisedPlayer)
      } else if (matchesAway || matchesAwayByName) {
        grouped[1].push(normalisedPlayer)
      } else if (teamName && homeTeam?.name?.toLowerCase().includes(teamName)) {
        grouped[0].push(normalisedPlayer)
      } else if (teamName && awayTeam?.name?.toLowerCase().includes(teamName)) {
        grouped[1].push(normalisedPlayer)
      } else {
        grouped[0].push(normalisedPlayer)
      }
    })

    if (enriched.teams?.[0]) {
      enriched.teams[0] = { ...enriched.teams[0], players: grouped[0] }
    }
    if (enriched.teams?.[1]) {
      enriched.teams[1] = { ...enriched.teams[1], players: grouped[1] }
    }
  }

  return enriched
}

export default function GameStatsHub({
  open,
  onToggle,
  onAddContext,
  onSidebarQuestion,
  contextData,
  onClearContext,
  isLoading,
  onClose
}) {
  const [toast, setToast] = useState(null)
  const [activeTab, setActiveTab] = useState('boxscore')
  const [selectedGame, setSelectedGame] = useState(null)
  const [expandedStats, setExpandedStats] = useState(true)
  // NFL: Always default to "This Week" (current week's games)
  // NBA: Default to "Today"
  const getDefaultDate = (league) => {
    if (league === 'nfl') {
      return 'today' // "This Wk" - shows all games from current NFL week
    }
    return 'today'
  }
  const [selectedLeague, setSelectedLeague] = useState(() => {
    // Restore league from localStorage on mount
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('selectedLeague')
      return saved === 'nfl' ? 'nfl' : 'nba'
    }
    return 'nba'
  })
  const [selectedDate, setSelectedDate] = useState(() => getDefaultDate('nba'))

  // Persist league selection to localStorage
  useEffect(() => {
    localStorage.setItem('selectedLeague', selectedLeague)
  }, [selectedLeague])

  // Clear selected game when date or league changes to avoid showing wrong game
  // Also update default date when switching to NFL on non-game days
  useEffect(() => {
    setSelectedGame(null)
    setSelectedDate(getDefaultDate(selectedLeague))
  }, [selectedLeague])

  // TanStack Query hooks - no more useEffect!
  // Use isLoading only for initial load, not refetches (prevents flash)
  const { data: gamesData, isLoading: gamesIsLoading, error: gamesError, refetch: refetchGames } = useLiveGames(selectedDate, selectedLeague)
  const gamesLoading = gamesIsLoading && !gamesData // Only show loading skeleton on initial load
  const selectedGameIsLive = selectedGame?.status?.toString().toUpperCase() === 'LIVE'
  const { data: gameStats, isLoading: statsLoading } = useGameStats(selectedGame?.id, selectedLeague, { isLive: selectedGameIsLive })
  const { data: oddsData } = useOdds()
  const { data: pulseData } = usePulse()
  const { prefetchAllPreviews, prefetchPulse } = usePrefetch()
  
  // Only prefetch pulse on games load (previews prefetch on hover instead)
  useEffect(() => {
    if (gamesData?.games?.length) {
      prefetchPulse(selectedLeague) // Prefetch pulse for tab switch
      // Don't prefetch all previews - causes 500 errors. Previews prefetch on hover instead.
    }
  }, [gamesData?.games, prefetchPulse, selectedLeague])
  
  // Pre-warm preview for selected game (not just hover)
  useEffect(() => {
    if (selectedGame?.id && selectedLeague) {
      prefetchAllPreviews([selectedGame])
    }
  }, [selectedGame?.id, selectedLeague, prefetchAllPreviews])
  
  // Extract games array and data status from response
  const rawGames = gamesData?.games || gamesData || []
  const gamesDataStatus = gamesData?.dataStatus || null
  const gamesLastPoll = gamesData?.lastPoll || gamesDataStatus?.lastPoll || null

  // IMPORTANT: oddsMap is a FALLBACK only - do NOT use it to overwrite game.odds!
  // Primary odds come from /api/games/with-odds which attaches odds directly to each game.
  // This separate useOdds() hook exists for edge cases where games don't have odds attached.
  // See commit 8983bb2 for the bug this caused when we overwrote game.odds with oddsMap.
  const oddsMap = useMemo(() => {
    const map = new Map()
    if (oddsData?.odds) {
      oddsData.odds.forEach(odd => {
        // Group by game_id, prefer DraftKings
        if (!map.has(odd.game_id) || odd.vendor === 'draftkings') {
          map.set(odd.game_id, odd)
        }
      })
    }
    return map
  }, [oddsData])

  // Normalize games data - odds come from /api/games/with-odds and are preserved in normalizeGame
  // oddsMap is only used as fallback for games that somehow don't have odds
  const games = useMemo(() => {
    let normalized = rawGames.map(normalizeGame).filter(Boolean)
    
    // Debug: log odds status
    const withOdds = normalized.filter(g => g.odds)
    console.log(`[Games] ${normalized.length} games, ${withOdds.length} with odds`)
    
    // Apply odds fallback
    normalized = normalized.map(game => {
      if (!game.odds && oddsMap.has(game.id)) {
        return { ...game, odds: oddsMap.get(game.id), oddsStatus: 'available' }
      }
      return game
    })
    
    // Sort NFL games by date: upcoming games first (sorted by startTime), then final games at bottom
    if (selectedLeague === 'nfl') {
      normalized.sort((a, b) => {
        const aFinal = (a.status || '').toLowerCase().includes('final')
        const bFinal = (b.status || '').toLowerCase().includes('final')
        // Final games go to bottom
        if (aFinal && !bFinal) return 1
        if (!aFinal && bFinal) return -1
        // Within same category, sort by startTime (earliest first)
        const aTime = new Date(a.raw?.startTime || a.date || 0).getTime()
        const bTime = new Date(b.raw?.startTime || b.date || 0).getTime()
        return aTime - bTime
      })
    }
    
    return normalized
  }, [rawGames, oddsMap, selectedLeague])

  // Enrich selected game with stats when they load
  // IMPORTANT: Always preserve the normalized status from selectedGame
  const enrichedSelectedGame = useMemo(() => {
    if (!selectedGame) return null
    if (!gameStats) return selectedGame
    const enriched = enrichGameWithStats(selectedGame, gameStats)
    // Force preserve normalized status - never let raw API status through
    enriched.status = selectedGame.status
    enriched.time = selectedGame.time
    return enriched
  }, [selectedGame, gameStats])

  // Filter injuries for the selected game's teams
  const relevantInjuries = useMemo(() => {
    if (!selectedGame || !pulseData?.items) return []
    
    const homeTeam = selectedGame.teams?.[0]?.name?.toLowerCase() || ''
    const awayTeam = selectedGame.teams?.[1]?.name?.toLowerCase() || ''
    const homeAbbr = (selectedGame.teams?.[0]?.code || selectedGame.teams?.[0]?.abbreviation || '').toLowerCase()
    const awayAbbr = (selectedGame.teams?.[1]?.code || selectedGame.teams?.[1]?.abbreviation || '').toLowerCase()
    
    return pulseData.items
      .filter(item => item.type === 'injury')
      .filter(item => {
        const team = (item.team || '').toLowerCase()
        const title = (item.title || '').toLowerCase()
        return team.includes(homeTeam) || team.includes(awayTeam) ||
               team.includes(homeAbbr) || team.includes(awayAbbr) ||
               title.includes(homeTeam) || title.includes(awayTeam)
      })
      .map(item => ({
        playerName: item.player || item.title?.split(' - ')[0] || 'Unknown',
        team: item.team || '',
        status: item.title?.split(' - ')[1] || item.description || 'Unknown'
      }))
  }, [selectedGame, pulseData])

  // Update context when game stats (including props) load
  useEffect(() => {
    if (enrichedSelectedGame && onAddContext) {
      onAddContext({
        id: enrichedSelectedGame.id,
        gameId: enrichedSelectedGame.id,
        title: `${enrichedSelectedGame.teams?.[1]?.name || 'Away'} @ ${enrichedSelectedGame.teams?.[0]?.name || 'Home'}`,
        game: enrichedSelectedGame,
        odds: enrichedSelectedGame.odds || null,
        playerProps: gameStats?.playerProps || [],
        injuries: relevantInjuries,
      })
    }
  }, [enrichedSelectedGame, gameStats, relevantInjuries, onAddContext])

  const buildContextForGame = (gameId, fallbackTitle) => {
    if (!gameId) return null
    const normalizedId = String(gameId)
    const matchedGame = games.find(g => String(g.id) === normalizedId) || null
    const title = fallbackTitle || (matchedGame
      ? `${matchedGame.teams?.[1]?.name || 'Away'} @ ${matchedGame.teams?.[0]?.name || 'Home'}`
      : 'Live feed question')
    return {
      id: normalizedId,
      gameId: normalizedId,
      title,
      game: matchedGame,
      odds: matchedGame?.odds || null,
      playerProps: matchedGame?.playerProps || [],
      injuries: [],
    }
  }

  const handleSidebarQuestion = (question, contextOverride = null) => {
    if (onSidebarQuestion) {
      onSidebarQuestion(question, contextOverride)
      setToast('Question sent to chat')
    }
  }

  const handleSelectGame = (game) => {
    setSelectedGame(game)
    setExpandedStats(true)
    // Set initial context immediately (useEffect will update with props/injuries when loaded)
    if (onAddContext && game) {
      onAddContext({
        id: game.id,
        gameId: game.id,
        title: `${game.teams?.[1]?.name || 'Away'} @ ${game.teams?.[0]?.name || 'Home'}`,
        game: game,
        odds: game.odds || null,
        playerProps: [], // Will be filled by useEffect
        injuries: [], // Will be filled by useEffect
      })
    }
  }

  const handleAskAI = (payload) => {
    if (!payload) return
    let question = ''
    let contextOverride = null

    if (typeof payload === 'string') {
      question = payload
      // For string questions from smart chips, use the currently selected game
      if (selectedGame?.id) {
        contextOverride = buildContextForGame(selectedGame.id, `${selectedGame.teams?.[1]?.name || 'Away'} @ ${selectedGame.teams?.[0]?.name || 'Home'}`)
      }
    } else {
      question = payload.question || payload.action ||
        (payload.play ? `Tell me about: ${payload.play}` : `Tell me about: ${payload.name || 'this play'}`)
      
      if (payload.gameId) {
        contextOverride = buildContextForGame(payload.gameId, payload.title)
      }
      
      // If payload has playerContext (from PlayerCardV2), include it
      if (payload.playerContext) {
        contextOverride = {
          ...contextOverride,
          playerContext: payload.playerContext,
        }
      }
      
      // If payload has injuryContext (from PulseTab injury click), include it
      if (payload.injuryContext) {
        contextOverride = {
          ...contextOverride,
          injuryContext: payload.injuryContext,
        }
      }
      
      // If payload has liveEventContext (from PulseTab live feed click), include it
      if (payload.liveEventContext) {
        contextOverride = {
          ...contextOverride,
          liveEventContext: payload.liveEventContext,
        }
      }
    }

    if (!question) {
      question = 'Tell me about this play.'
    }

    if (contextOverride && onAddContext) {
      onAddContext(contextOverride)
    }

    handleSidebarQuestion(question, contextOverride)
  }

  const handleFollow = (gameId) => {
    setToast(`Game ${gameId} ${selectedGame?.id === gameId ? 'followed' : 'unfollowed'}`)
  }

  const gameLeague = enrichedSelectedGame?.league || enrichedSelectedGame?.raw?.league || FALLBACK_LEAGUE

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
      {/* Tab header - 50/50 toggle like footer NBA/NFL */}
      <div className="shrink-0 px-4 py-2.5 flex justify-center" style={{ borderBottom: '1px solid #F3F4F6' }}>
        <div className="flex items-center rounded-lg p-0.5" style={{ backgroundColor: '#F3F4F6' }}>
          <button
            onClick={() => { setActiveTab('boxscore'); onClearContext?.() }}
            className="flex-1 px-4 py-1.5 rounded-md text-[12px] font-medium transition-all"
            style={{
              background: activeTab === 'boxscore' ? '#FFFFFF' : 'transparent',
              color: activeTab === 'boxscore' ? '#1F2937' : '#9CA3AF',
              boxShadow: activeTab === 'boxscore' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            {selectedGame 
              ? (selectedGame.status === 'SCHEDULED' || selectedGame.status === 'UPCOMING' ? 'Preview' : 'Stats') 
              : 'Home'}
          </button>
          <button
            onClick={() => { setActiveTab('feed'); onClearContext?.() }}
            className="flex-1 px-4 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center justify-center gap-1.5"
            style={{
              background: activeTab === 'feed' ? '#FFFFFF' : 'transparent',
              color: activeTab === 'feed' ? '#FF4D6D' : '#9CA3AF',
              boxShadow: activeTab === 'feed' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-60"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pink-500"></span>
            </span>
            Pulse
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch', backgroundColor: '#FAFAFA' }}>
        {activeTab === 'boxscore' && (
          <>
            {selectedGame ? (
              <div className="space-y-3">
                <GameDetailDrawer
                  game={enrichedSelectedGame || selectedGame}
                  league={selectedLeague}
                  onAddContext={onAddContext}
                  isExpanded={expandedStats}
                  onToggle={() => setExpandedStats(!expandedStats)}
                  onBack={() => {
                    setSelectedGame(null)
                    setExpandedStats(false)
                  }}
                  onAskAI={handleAskAI}
                />
                {/* Stats loading handled by GameDetailDrawer */}
              </div>
            ) : (
              <GamesTab
                games={games}
                isLoading={gamesLoading}
                error={gamesError?.message}
                onSelectGame={handleSelectGame}
                onAskAI={handleAskAI}
                onFollow={handleFollow}
                onRefresh={refetchGames}
                selectedGame={selectedGame}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                selectedLeague={selectedLeague}
                onLeagueChange={setSelectedLeague}
                lastPoll={gamesLastPoll}
                dataStatus={gamesDataStatus}
              />
            )}
          </>
        )}

        {activeTab === 'feed' && (
          <PulseTab
            games={games}
            onAskAI={handleAskAI}
            isLiveConnected={false}
            selectedLeague={selectedLeague}
            onLeagueChange={setSelectedLeague}
          />
        )}
      </div>

      {/* Footer - Single thin row with 2-column layout */}
      <div className="shrink-0 px-3 py-1.5 flex items-center justify-between" style={{ borderTop: '1px solid #F3F4F6', backgroundColor: '#FAFAFA' }}>
        {/* Left: NBA/NFL toggle */}
        <div className="flex items-center">
          <div className="flex items-center rounded p-0.5" style={{ backgroundColor: '#F3F4F6' }}>
            {['nba', 'nfl'].map((league) => {
              const isActive = (selectedLeague || 'nba') === league
              return (
                <button
                  key={league}
                  onClick={() => setSelectedLeague(league)}
                  className="px-2 py-0.5 rounded text-[9px] font-semibold transition-all"
                  style={{
                    background: isActive ? '#FFFFFF' : 'transparent',
                    color: isActive ? '#1F2937' : '#9CA3AF',
                    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                >
                  {league.toUpperCase()}
                </button>
              )
            })}
          </div>
        </div>
        
        {/* Right: Date nav (only when on Home and no game selected) */}
        <div className="flex items-center gap-1">
          {activeTab === 'boxscore' && !selectedGame && (
            <>
              {selectedLeague === 'nfl' ? (
                /* NFL: Week-based tabs - stable, works every week */
                <>
                  <button
                    onClick={() => setSelectedDate('yesterday')}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors"
                    style={{ color: selectedDate === 'yesterday' ? '#FF4D6D' : '#9CA3AF' }}
                  >
                    Prev Wk
                  </button>
                  <button
                    onClick={() => setSelectedDate('today')}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors"
                    style={{ color: selectedDate === 'today' ? '#FF4D6D' : '#9CA3AF' }}
                  >
                    This Wk
                  </button>
                  <button
                    onClick={() => setSelectedDate('next')}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors"
                    style={{ color: selectedDate === 'next' ? '#FF4D6D' : '#9CA3AF' }}
                  >
                    Next Wk
                  </button>
                </>
              ) : (
                /* NBA: Day-based tabs */
                <>
                  <button
                    onClick={() => setSelectedDate('yesterday')}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors"
                    style={{ color: selectedDate === 'yesterday' ? '#FF4D6D' : '#9CA3AF' }}
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setSelectedDate('today')}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors"
                    style={{ color: selectedDate === 'today' ? '#FF4D6D' : '#9CA3AF' }}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => setSelectedDate('tomorrow')}
                    className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors"
                    style={{ color: selectedDate === 'tomorrow' ? '#FF4D6D' : '#9CA3AF' }}
                  >
                    Next
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  )
}
